/**
 * Demo-omgeving: volledig los van de echte app.
 * Gebruikt alleen /api/demo/* en eigen context; deelt geen bestanden of state met de echte omgeving.
 */
import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { getApiBaseUrl } from '../utils/axios';
import QuotationWizard from './QuotationWizard';
import './DemoLayout.css';

const DEMO_EMAIL_KEY = 'demoEmail';

function DemoEmailGate({ onAccess }) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setError('Vul een geldig e-mailadres in.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const base = getApiBaseUrl();
      const res = await fetch(`${base}/api/demo/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
        credentials: 'include'
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.error || 'Registratie mislukt.');
        return;
      }
      try { sessionStorage.setItem(DEMO_EMAIL_KEY, email); } catch (_) {}
      onAccess(email);
    } catch (err) {
      setError('Verbinding mislukt. Probeer het opnieuw.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="demo-email-gate">
      <div className="demo-email-card">
        <img src="/logo-offerterunner.png" alt="OfferteRunner" className="demo-email-logo" />
        <h2>Demo OfferteRunner</h2>
        <p>Vul uw e-mailadres in om de demo te starten. Geen wachtwoord nodig.</p>
        <form onSubmit={handleSubmit}>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="uw@emailadres.nl"
            autoFocus
            required
          />
          {error && <div className="demo-email-error">{error}</div>}
          <button type="submit" disabled={submitting}>
            {submitting ? 'Bezig...' : 'Start demo'}
          </button>
        </form>
      </div>
    </div>
  );
}

function DemoAuthOverride({ children }) {
  const navigate = useNavigate();
  const [subscribers, setSubscribers] = useState([]);
  const [subscribersLoaded, setSubscribersLoaded] = useState(false);
  const [selectedSubscriberId, setSelectedSubscriberIdState] = useState('');

  const setSelectedSubscriberId = useCallback((id) => {
    setSelectedSubscriberIdState(id || '');
    if (id) {
      try {
        localStorage.setItem('selectedSubscriberId', id);
      } catch (_) {}
    }
  }, []);

  const loadSubscribers = useCallback(async () => {
    try {
      const base = getApiBaseUrl();
      const res = await fetch(`${base}/api/demo/subscribers`, { credentials: 'include' });
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      setSubscribers(list);
      if (list.length > 0) {
        const firstId = list[0].id;
        setSelectedSubscriberIdState(firstId);
        try {
          localStorage.setItem('selectedSubscriberId', firstId);
        } catch (_) {}
      }
    } catch (err) {
      console.error('Demo loadSubscribers error:', err);
      setSubscribers([]);
    } finally {
      setSubscribersLoaded(true);
    }
  }, []);

  useEffect(() => {
    loadSubscribers();
  }, []);

  // Registreer "demo geopend" voor statistieken
  useEffect(() => {
    const base = getApiBaseUrl();
    fetch(`${base}/api/demo/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event: 'opened' }),
      credentials: 'include'
    }).catch(() => {});
  }, []);

  const leaveDemo = useCallback(() => {
    try {
      localStorage.removeItem('selectedSubscriberId');
    } catch (_) {}
    navigate('/', { replace: true });
    window.location.reload();
  }, [navigate]);

  const demoUser = {
    role: 'subscriber',
    id: 'demo',
    isDemo: true,
    email: 'demo@offerterunner.nl'
  };

  const value = {
    user: demoUser,
    loading: false,
    isUnderConstruction: false,
    backendReachable: true,
    login: () => {},
    logout: leaveDemo,
    checkAuth: () => {},
    subscribers,
    subscribersLoaded,
    selectedSubscriberId: selectedSubscriberId || (subscribers[0] && subscribers[0].id),
    setSelectedSubscriberId,
    loadSubscribers,
    isLicenseActive: true,
    trialEndsAt: null,
    isTrialExpired: false,
    trialDaysLeft: null
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

function DemoLayoutContent() {
  const navigate = useNavigate();
  const { subscribers, selectedSubscriberId, setSelectedSubscriberId, subscribersLoaded } = useContext(AuthContext);
  const [wizardKey, setWizardKey] = useState(0);

  const hasSubscribers = subscribersLoaded && subscribers && subscribers.length > 0;
  const currentSubscriberId = selectedSubscriberId || (subscribers && subscribers[0] && subscribers[0].id);

  const leaveDemo = () => {
    navigate('/');
    window.location.reload();
  };

  return (
    <div className="App demo-app">
      <div className="demo-banner" role="banner">
        <span className="demo-banner-label">Demo</span>
        <span className="demo-banner-text">Schilderbedrijf Kwastie – geen inlog, geen Odoo</span>
        <button type="button" className="demo-banner-leave" onClick={leaveDemo}>
          Verlaat demo
        </button>
      </div>

      <div className="app-header">
        <div className="header-content">
          <img src="/logo-offerterunner.png" alt="OfferteRunner" className="header-logo" />
          <div className="header-right">
            <div className="header-nav">
              <button
                type="button"
                className="btn btn-primary btn-small nav-btn"
                onClick={() => setWizardKey(k => k + 1)}
              >
                <span className="nav-btn-icon" aria-hidden>➕</span>
                Nieuwe offerte
              </button>
              <span className="nav-btn demo-disabled" title="Alleen in de echte app">
                📄 Offertes
              </span>
              <span className="nav-btn demo-disabled" title="Alleen in de echte app">
                📥 Aanvragen
              </span>
              <span className="nav-btn demo-disabled" title="Alleen in de demo beperkt">
                ⚙️ Instellingen
              </span>
              <button
                type="button"
                className="btn btn-secondary btn-small nav-btn"
                onClick={leaveDemo}
              >
                <span className="nav-btn-icon" aria-hidden>🚪</span>
                Verlaat demo
              </button>
            </div>
            <div className="header-toolbar demo-toolbar">
              {hasSubscribers && (
                <label className="header-bedrijf">
                  <span className="header-bedrijf-label">Bedrijf:</span>
                  <select
                    className="header-bedrijf-select"
                    value={currentSubscriberId}
                    onChange={(e) => setSelectedSubscriberId(e.target.value)}
                  >
                    {(subscribers || []).map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.company_name || s.companyName || 'Onbenoemd'}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="wizard-content">
        {hasSubscribers ? (
          <QuotationWizard key={wizardKey} />
        ) : (
          <div className="demo-loading">Demo laden...</div>
        )}
      </div>
    </div>
  );
}

export default function DemoLayout() {
  const [emailGranted, setEmailGranted] = useState(() => {
    try { return !!sessionStorage.getItem(DEMO_EMAIL_KEY); } catch (_) { return false; }
  });

  if (!emailGranted) {
    return <DemoEmailGate onAccess={() => setEmailGranted(true)} />;
  }

  return (
    <DemoAuthOverride>
      <DemoLayoutContent />
    </DemoAuthOverride>
  );
}
